// Typed, validated access to the plumber2 API. Same-origin in production; in dev
// the Vite proxy forwards these paths to plumber (see vite.config.ts).
//
// Validation policy: fetchers `safeParse` against the zod schemas. On a mismatch
// they warn (so contract drift is visible in dev) and fall back to the raw value,
// so a minor API change degrades gracefully instead of blanking the UI. Tests use
// strict `.parse` on the captured fixtures to assert the contract holds.
import type { z } from "zod";
import { Catalog, Categories, DatasetMeta, Maps, SeriesResponse } from "./schemas";

export const API = ""; // same-origin

async function jget(path: string): Promise<unknown> {
  const r = await fetch(API + path);
  if (!r.ok) throw new Error(`${path} -> ${r.status}`);
  return r.json();
}

function validate<S extends z.ZodTypeAny>(
  schema: S,
  data: unknown,
  ctx: string,
): z.infer<S> {
  const res = schema.safeParse(data);
  if (!res.success) {
    console.warn(`[api] ${ctx} did not match its schema:`, res.error.issues.slice(0, 5));
    return data as z.infer<S>;
  }
  return res.data;
}

export async function fetchCatalog() {
  return validate(Catalog, await jget("/catalog"), "/catalog");
}

export async function fetchCategories() {
  return validate(Categories, await jget("/categories"), "/categories");
}

export async function fetchMaps() {
  return validate(Maps, await jget("/maps"), "/maps");
}

export async function fetchMeta(id: string) {
  return validate(DatasetMeta, await jget(`/dataset/${id}/meta`), `/dataset/${id}/meta`);
}

/** dims object -> grammar string, e.g. { D0: ["T0","S0"] } -> "D0=T0,S0". */
export const dimsQuery = (dims: Record<string, string[]>): string =>
  Object.entries(dims)
    .map(([d, codes]) => `${d}=${codes.join(",")}`)
    .join(";");

/** A dims string ready for a query string. Level codes may carry "+" (ch_fso_besta
 * "77+79-82"), spaces or "&"; unescaped, "+" reads as a space and the level
 * matches nothing. The grammar's own = ; , and / stay readable, since these URLs
 * are shown to people in the code snippets. */
export const encodeDims = (dims: string): string =>
  encodeURIComponent(dims).replace(/%3D/g, "=").replace(/%3B/g, ";").replace(/%2C/g, ",").replace(/%2F/g, "/");

export interface SeriesQuery {
  dataset: string;
  dims?: Record<string, string[]>;
  from?: string;
  to?: string;
}

function seriesQueryString(q: SeriesQuery): string {
  const parts = [`dataset=${encodeURIComponent(q.dataset)}`];
  const dims = q.dims ? dimsQuery(q.dims) : "";
  if (dims) parts.push(`dims=${encodeDims(dims)}`);
  if (q.from) parts.push(`from=${q.from}`);
  if (q.to) parts.push(`to=${q.to}`);
  return parts.join("&");
}

export async function fetchSeries(q: SeriesQuery) {
  return validate(SeriesResponse, await jget(`/series?${seriesQueryString(q)}`), "/series");
}

/** Public tidy-CSV URL for one series spec (consumed by the Access snippets).
 *  Same canonical origin as everything else (api.dataseries.org was retired);
 *  absolute so the snippet is copy-pasteable. Falls back to prod when there is
 *  no window (SSR/tests). */
export const CSV_ORIGIN =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://dataseries.org";
export const csvUrl = (s: { id: string; dims: Record<string, string[]> }): string => {
  const q = dimsQuery(s.dims);
  return `${CSV_ORIGIN}/series.csv?dataset=${s.id}` + (q ? `&dims=${encodeDims(q)}` : "");
};
